import { Box, InputAdornment, TextField, Typography, MenuItem, Popper, Paper, Skeleton } from "@mui/material";
import { useState, useRef, useEffect, useCallback } from "react";
import { AttachmentIcon, CloseIcon, SendMessageIcon } from "../../../assets/CommonSvg";
import { ImageIcon, PdfIcon, DocumentIcon, FileIcon } from "../../../../assets/common-svg/CommonSvg";
import { fetchChatHistory, selectAllChatRoom, selectMediaLoading, updateAllChatRoom, uploadChatFiles } from "../../../slices/chat";
import { useDispatch, useSelector } from "react-redux";
import ChatHistory from "./chatHistory";
import { MAX_FILE_SIZE, SUPPORTED_FORMATS } from "../../../../pages/appointment-details/common-pre-screen/common";
import { useSocketInstance } from "../../../../context/SocketContext";
import { formatMessage } from "../common";
import { useVisibilityStatus } from "../../../hooks/useVisibilityStatus";

const ChatBody = ({ selectedRoom }) => {
    const dispatch = useDispatch();
    const { loginId } = useSelector(
        (state) => state.loginData
    );
    const allChatRoom = useSelector(selectAllChatRoom)
    const isMediaLoading = useSelector(selectMediaLoading)
    const { socketInstance } = useSocketInstance()
    const [message, setMessage] = useState("");
    const [messages, setMessages] = useState([]);
    const [mentionQuery, setMentionQuery] = useState("");
    const [showMentionList, setShowMentionList] = useState(false);
    const [mentionPosition, setMentionPosition] = useState(null);
    const [mentionStartPos, setMentionStartPos] = useState(-1);
    const [attachments, setAttachments] = useState([]);
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [error, setError] = useState("");
    const fileInputRef = useRef(null);
    const inputRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const { ref } = useVisibilityStatus()

    const getChatHistory = async (pageNum = 1, initialLoad = false) => {
        try {
            const container = scrollContainerRef.current;
            let prevScrollHeight = 0;
            let prevScrollTop = 0;

            if (container && !initialLoad) {
                prevScrollHeight = container.scrollHeight;
                prevScrollTop = container.scrollTop;
            }

            const response = await dispatch(fetchChatHistory({
                roomId: selectedRoom?.id,
                params: { page: pageNum, per_page: 10 }
            })).unwrap();

            if (response?.status === 200) {
                const newMessages = response.data.items.map(item =>
                    formatMessage(item, loginId)
                );

                if (initialLoad) {
                    setMessages(newMessages);
                    setTimeout(() => {
                        if (container) container.scrollTop = container.scrollHeight;
                    }, 0);
                } else {
                    setMessages(prev => [...newMessages, ...prev]);
                    setTimeout(() => {
                        if (container) {
                            const newScrollHeight = container.scrollHeight;
                            const scrollOffset = newScrollHeight - prevScrollHeight;
                            container.scrollTop = scrollOffset + prevScrollTop;
                        }
                    }, 0);
                }

                setHasMore(response.data.total_count > pageNum * response.data.per_page);
            }
        } catch (err) {
            console.error(err);
        }
    };


    const fetchMoreData = () => {
        const nextPage = page + 1;
        getChatHistory(nextPage);
        setPage(nextPage);
    };

    useEffect(() => {
        if (selectedRoom) {
            setPage(1);
            setMessages([]);
            getChatHistory(1, true);
        }
    }, [selectedRoom]);


    const handleReceiveMessage = useCallback(async (data) => {
        const sender = selectedRoom?.members?.find(op => op.id == data?.sender_id)
        data.sender_name = sender?.name
        data.sender_role = sender?.role
        if (data.chat_room_id === selectedRoom?.id) {
            // ✅ Scroll to bottom when sender is current user
      if (data?.sender_id === loginId && scrollContainerRef.current) {
        setTimeout(() => {
          scrollContainerRef.current.scrollTop =
            scrollContainerRef.current.scrollHeight;
        }, 100); // small delay to ensure DOM updates
      }
            const messageData = formatMessage(data, loginId)
            setMessages(prev => [...prev, messageData]);
        }
         if(data?.sender_id === loginId || selectedRoom?.id == data?.chat_room_id)
        socketInstance.emit("markMessagesAsRead", {chat_room_id: data?.chat_room_id, name: data?.chat_room_name });
    }, []);

    useEffect(() => {
        if (!socketInstance) return;
        socketInstance.on("receive_room_message", handleReceiveMessage);
        socketInstance.emit("markMessagesAsRead", {chat_room_id: selectedRoom?.id, name: selectedRoom?.name });
        const updatedItems = allChatRoom.map(item => {
            if (selectedRoom?.id === item.id) {
                return { ...item, unread_count: 0 };
            } else {
                return item
            }
        });
        dispatch(updateAllChatRoom(updatedItems))
        return () => {
            socketInstance.off("receive_room_message", handleReceiveMessage);
        };
    }, [socketInstance, selectedRoom, loginId]);


    const filteredMembers = selectedRoom?.members?.filter(user =>
        user?.id !== loginId && // exclude the user with matching id
        user?.name?.toLowerCase().includes(mentionQuery?.toLowerCase())
    );

    const handleSendMessage = () => {
        if (message.trim() === "" && attachments.length === 0) {
            setError("Message or attachment is required");
            return;
        }
        setError("");
        if (socketInstance) {
            socketInstance.emit("send_room_message", {
                chat_room_id: selectedRoom?.id,
                name: selectedRoom?.name,
                message: message,
                mentions: selectedMembers,
                media_id: attachments.map(op => op.id), // Include attachment IDs if needed
                message_type: attachments.length > 0 ? 2 : 1  // 2 for file and msg both // 1 for messge 
            });
        }
        setSelectedMembers([])
        setMessage("");
        setAttachments([]);
        setShowMentionList(false);
        setMentionStartPos(-1);
    };

    const handleAttachmentClick = () => {
        fileInputRef.current.click();
    };

    const handleUploadFiles = async (e) => {
        const { files } = e.target;

        const AllFiles = Array.from(files);

        if (AllFiles?.length !== 0) {
            const validFiles = AllFiles?.filter(file =>
                SUPPORTED_FORMATS.includes(file.type) &&
                file.size <= MAX_FILE_SIZE
            );

            if (files.length > 0 && validFiles.length === 0) {
                setError("Files must be supported format and under 25MB");
                return;
            }

            try {
                const formData = new FormData();
                formData.append(`files`, validFiles[0]);
                const res = await dispatch(uploadChatFiles(formData)).unwrap();
                const newAttachments = {
                    id: res?.media_id,
                    name: res?.message,
                    type: res.type.split('/')[0], // 'image', 'application', etc.
                }

                setAttachments([...attachments, ...[newAttachments]]);
                setError("");
            } catch (err) {
                console.error(err);
                setError("Failed to upload files. Please try again.");
            } finally {
                // Clear the file input to allow selecting the same files again if needed
                e.target.value = '';
            }
        }
    };

    const removeAttachment = (id) => {
        setAttachments(attachments?.filter(attachment => attachment.id !== id));
    };


    const handleInputChange = (e) => {
        const value = e.target.value;
        setMessage(value);
        setError("");

        const cursorPosition = e.target.selectionStart;
        const textBeforeCursor = value.substring(0, cursorPosition);

        const atSymbolIndex = value.lastIndexOf("@");
        const isMentionStart = value.trim().endsWith("@");
        if (isMentionStart) {
            setMentionStartPos(atSymbolIndex);
            const query = textBeforeCursor.substring(atSymbolIndex + 1);
            setMentionQuery(query);

            const inputRect = inputRef.current.getBoundingClientRect();
            const charWidth = 8;
            setMentionPosition({
                top: inputRect.top - 200,
                left: inputRect.left + (atSymbolIndex * charWidth)
            });

            setShowMentionList(true);
        } else if (mentionStartPos >= 0 && cursorPosition > mentionStartPos) {
            const query = value.substring(mentionStartPos + 1, cursorPosition);
            setMentionQuery(query);
        } else {
            setShowMentionList(false);
            setMentionStartPos(-1);
        }
    };

    const handleMemberSelect = (member) => {
        if (mentionStartPos < 0) return;

        const newMessage =
            message.substring(0, mentionStartPos) +
            `@${member.name} ` +
            message.substring(inputRef.current.selectionStart);

        setMessage(newMessage);
        setShowMentionList(false);
        setMentionStartPos(-1);
        setSelectedMembers([...selectedMembers, member])
        setTimeout(() => {
            inputRef.current.focus();
            const newCursorPos = mentionStartPos + member.name.length + 2;
            inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
    };

    const renderSkeleton = () => {
        return Array(1).fill(0)?.map((_, index) => (
            <div key={`skeleton-${index}`} className='pt-3 pb-3 border-b flex gap-2 items-center justify-between'>
                <div className='flex gap-2 items-center w-full'>
                    <div className="w-full p-2">
                        <Skeleton variant="text" width="100%" height={20} />
                        <Skeleton variant="text" width="40%" height={15} />
                        <Skeleton variant="text" width="100%" height={20} />
                        <Skeleton variant="text" width="40%" height={15} />
                        <Skeleton variant="text" width="100%" height={20} />
                    </div>
                </div>
            </div>
        ));
    };
    return (
        <>
            <ChatHistory
                ref={scrollContainerRef}
                messages={messages}
                hasMore={hasMore}
                fetchMoreData={fetchMoreData}
            />
            <div className="relative" >
                {isMediaLoading ? renderSkeleton() : attachments.length > 0 && (
                    <div className="flex flex-wrap gap-2 p-3 bg-gray-100 border-b border w-100">
                        {attachments?.map((attachment, index) => (
                            <div key={index} className="flex gap-2">
                               <div>
                                    {(() => {
                                        const fileName = attachment?.name || ""; // in case name differs
                                        const extension = fileName?.split('.')?.pop()?.toLowerCase();
                                        if (attachment?.type === 'image') {
                                        return <ImageIcon width={25} height={25} />;
                                        } else if (['pdf'].includes(extension)) {
                                        return <PdfIcon width={25} height={25} />;
                                        } else if (['doc', 'docx'].includes(extension)) {
                                        return <DocumentIcon width={25} height={25} />; // Replace with your document icon component
                                        } else {
                                        return <FileIcon width={25} height={25} />; // fallback icon
                                        }
                                    })()}
                                </div>
                                <div className="flex gap-2 items-center">
                                    <span className="text-wrap text-[0.8rem]">
                                        {attachment.name.length > 35 ?
                                            `${attachment.name.substring(0, 35)}...` :
                                            attachment.name
                                        }
                                    </span>
                                    <span className="cursor-pointer" onClick={() => removeAttachment(attachment.id)}>  <CloseIcon width={9} height={9} /></span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {error && (
                    <Typography color="error" variant="caption" className="block px-2">
                        {error}
                    </Typography>
                )}
                <Box className="flex" ref={ref}>
                    <TextField
                        multiline
                        minRows={1}
                        maxRows={6}
                        inputRef={inputRef}
                        variant="outlined"
                        className="bg-[#F0F1F5] w-full py-[10.5px] border-0 "
                        size="small"
                        placeholder="Type something here..."
                        value={message}
                        onChange={handleInputChange}
                        error={!!error}
                        sx={{ "& fieldset": { border: 'none' } }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end" className="flex gap-2 items-center">
                                    <button
                                        className="cursor-pointer"
                                        onClick={handleAttachmentClick}
                                        type="button"
                                    >
                                        <AttachmentIcon className="text-gray-500" />
                                    </button>
                                    <button
                                        className="cursor-pointer"
                                        onClick={handleSendMessage}
                                    >
                                        <SendMessageIcon className="text-gray-500" />
                                    </button>
                                </InputAdornment>
                            )
                        }}
                    />
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleUploadFiles}
                        style={{ display: 'none' }}
                        // multiple
                        accept={SUPPORTED_FORMATS.join(",")}
                    />
                </Box>

                {showMentionList && (
                    <Popper
                        open={showMentionList}
                        anchorEl={inputRef.current}
                        placement="top-start"
                        style={{
                            position: 'absolute',
                            zIndex: 1300,
                            top: mentionPosition?.top,
                            left: mentionPosition?.left
                        }}
                    >
                        <Paper elevation={3} className="max-h-60 overflow-y-auto w-48">
                            {filteredMembers?.length > 0 ? (
                                filteredMembers?.map(member => (
                                    <MenuItem
                                        key={member.id}
                                        onClick={() => handleMemberSelect(member)}
                                        className="flex items-center gap-2"
                                    >
                                        <div>
                                            <Typography variant="body2">{member.name}</Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {member.role}
                                            </Typography>
                                        </div>
                                    </MenuItem>
                                ))
                            ) : (
                                <MenuItem disabled>
                                    <Typography variant="body2">No matches</Typography>
                                </MenuItem>
                            )}
                        </Paper>
                    </Popper>
                )}
            </div>
        </>
    );
};

export default ChatBody;